<template>
  <div class="program-selector__wrapper">
    <div class="program-selector__title">
      <h1 class="program-selector__title-text">
        Ответьте на 4 вопроса, а мы подберем вам индивидуальную программу в
        режиме онлайн
      </h1>
    </div>
    <div v-if="programsSetup" class="program-selector">
      <div
        v-if="currentStep === 0"
        class="question-block question-block__first"
      >
        <div class="question-block__top">
          <span>1</span>
          <h2>Какой для вас удобен филиал?</h2>
        </div>
        <ul>
          <li v-for="filial in filialsList" :key="filial.value">
            <div
              class="question-block__label"
              @click="userForm.filialId = filial.value"
            >
              <img :src="filial.image" />
              <SmallButton
                :active="userForm.filialId === filial.value"
                size="md"
              >
                {{ filial.title }}
              </SmallButton>
            </div>
          </li>
        </ul>
      </div>

      <div v-if="currentStep === 1" class="question-block">
        <div class="question-block__top">
          <span>2</span>
          <h2>О вас</h2>
        </div>
        <div class="question-select">
          <span class="question-select__label">Пол</span>
          <div class="question-select__button-select">
            <SmallButton
              @click="userForm.genderId = gender.value"
              size="md"
              v-for="gender in genderList"
              :key="gender.value"
              :active="userForm.genderId === gender.value"
            >
              {{ gender.title }}
            </SmallButton>
          </div>
        </div>

        <div v-if="agesList.length" class="question-select">
          <span class="question-select__label">Возраст</span>
          <div class="question-select__list">
            <SmallButton
              @click="userForm.age = age.value"
              size="sm"
              v-for="age in agesList"
              :key="age.gender_id + age.title"
              :active="userForm.age === age.value"
            >
              {{ age.title }}
            </SmallButton>
          </div>
        </div>
      </div>

      <div v-if="currentStep === 2" class="question-block">
        <div class="question-block__top">
          <span>3</span>
          <h2>Профилактическое обследование</h2>
        </div>
        <TheLoader v-if="isLoading">Подготовка пожеланий</TheLoader>
        <div v-else>
          <RegularCheckUpList
            :modelValue="programsRegularCheckup"
            :programsSetup="programsSetup"
            @updateSelectedPrograms="updateSelectedProgramGroups"
            @openProgramGroupsInfo="
              (...args) => $emit('openProgramGroupsInfo', ...args)
            "
          />
        </div>
      </div>

      <div v-if="currentStep >= 3" class="question-block">
        <div class="question-block__top">
          <span>4</span>
          <h2>Персонализированные программы</h2>
        </div>
        <TheLoader v-if="isLoading">Подготовка пожеланий</TheLoader>
        <div v-else>
          <PersonalCheckupList
            :modelValue="programsPersonalCheckup"
            :programsSetup="programsSetup"
            @updateSelectedPrograms="updateSelectedProgramGroups"
            @openProgramGroupsInfo="
              (...args) => $emit('openProgramGroupsInfo', ...args)
            "
          />
        </div>
      </div>
      <div class="buttons-wrapper">
        <ErrorBlock
          class="program-selector__error-block"
          v-if="errorMessage || programsSetupError"
        >
          {{ errorMessage }}
          {{ programsSetupError }}
        </ErrorBlock>
        <button
          :disabled="currentStep === 0"
          @click="currentStep--"
          type="button"
          :class="{ 'disabled-button': currentStep === 0 }"
          class="program-btn-prev"
          style="display: flex"
        >
          <svg
            class="program-btn-prev-icon"
            :class="{ 'disabled-icon': currentStep === 0 }"
          >
            <use xlink:href="/uploads/img/sprite.svg#icon-arrow-nav"></use>
          </svg>
        </button>

        <button class="program-selector__reset-btn" @click="handleNextStepBtn">
          {{ currentStep === 3 ? "Составить чекап" : "далее" }}
        </button>
      </div>
    </div>

    <TheLoader v-else>Загрузка</TheLoader>
  </div>
</template>
<script>
import { computed, ref, watch, watchEffect, nextTick } from 'vue'
import TheLoader from '@/views/programs/checkup/components/TheLoader.vue'
import { getProgramsCheckup } from '@/views/programs/checkup/api/getProgramsCheckup.js'
import ErrorBlock from './ErrorBlock.vue'
import SmallButton from './SmallButton.vue'
import RegularCheckUpList from './RegularCheckUpList.vue'
import PersonalCheckupList from './PersonalCheckupList.vue'

export default {
  name: 'ProgramForm',
  components: {
    TheLoader,
    PersonalCheckupList,
    RegularCheckUpList,
    ErrorBlock,
    SmallButton
  },
  props: {
    programsSetup: [Object || null],
    programsSetupError: [String || Object || null]
  },
  emits: [
    'submitSelectedProgramGroups',
    'updateUserData',
    'submitProgramsRegularCheckup',
    'submitProgramsPersonalCheckup'
  ],
  setup (props, { emit }) {
    const userForm = ref({
      gender: null,
      age: null,
      filialId: null
    })

    const currentStep = ref(0)

    const isLoading = ref(false)
    const errorMessage = ref(null)

    const programGroups = ref(null)
    const programsData = ref(null)
    const programsRegularCheckup = ref(null)
    const programsPersonalCheckup = ref(null)
    const allProgramGroups = ref([])

    const onUserFormChange = (questionNum) => {
      currentStep.value = questionNum
    }

    const getProgramSelection = async () => {
      isLoading.value = true
      programGroups.value = null
      programsData.value = null
      try {
        programsData.value = await getProgramsCheckup({
          genderId: userForm.value.genderId,
          age: userForm.value.age,
          filialId: userForm.value.filialId
        })

        programsRegularCheckup.value = programsData.value[1].map((p) => ({
          title: p.title,
          image: p.image,
          id: p.id,
          items: p.items,
          isSelected: false
        }))
        programsPersonalCheckup.value = programsData.value[2].map((p) => ({
          title: p.title,
          id: p.id,
          image: p.image,
          items: p.items,
          isSelected: false
        }))

        allProgramGroups.value = programsRegularCheckup.value.concat(
          programsPersonalCheckup.value
        )
      } catch (err) {
        errorMessage.value = err
      } finally {
        isLoading.value = false
      }
    }

    const programsRegularSelected = computed(() =>
      programsRegularCheckup.value?.filter((p) => p.isSelected)
    )

    const handleNextStepBtn = async () => {
      if (currentStep.value === 3) {
        if (allProgramGroups.value.filter((p) => p.isSelected).length) {
          emit('updateUserData', userForm.value)
          emit(
            'submitSelectedProgramGroups',
            allProgramGroups.value.filter((p) => p.isSelected)
          )

          emit('submitProgramsRegularCheckup', programsRegularCheckup.value)
          emit('submitProgramsPersonalCheckup', programsPersonalCheckup.value)
          scrollToApp()
        } else {
          errorMessage.value = 'Выберите одну или несколько программ'
        }
      } else if (currentStep.value === 0 && !userForm.value.filialId) {
        errorMessage.value = 'Выберите филиал'
      } else if (currentStep.value === 1 && !userForm.value.genderId) {
        errorMessage.value = 'Укажите ваш пол'
      } else if (currentStep.value === 1 && !userForm.value.age) {
        errorMessage.value = 'Укажите ваш возраст'
      } else {
        currentStep.value++
        scrollToApp()
      }
    }

    const updateSelectedProgramGroups = (programs) => {
      allProgramGroups.value.forEach((program1) => {
        programs.forEach((program2) => {
          if (program1.id === program2.id) {
            program1.isSelected = program2.isSelected
          }
        })
      })
    }

    const filialsList = computed(() => {
      if (props.programsSetup) {
        return props.programsSetup.filial_id
      } else {
        return []
      }
    })

    const genderList = computed(() => {
      if (props.programsSetup) {
        return props.programsSetup.gender_id
      } else {
        return []
      }
    })

    const agesList = computed(() => {
      if (props.programsSetup) {
        return props.programsSetup.age.filter(
          (age) => userForm.value.genderId === age.gender_id
        )
      } else {
        return []
      }
    })

    const checkupTypesList = computed(() => {
      if (props.programsSetup) {
        return props.programsSetup.type_id
      } else {
        return []
      }
    })

    watchEffect(async () => {
      errorMessage.value = null
      if (
        userForm.value.genderId &&
        userForm.value.age &&
        userForm.value.filialId
      ) {
        await getProgramSelection()
      }
    })

    watchEffect(async () => {
      if (
        userForm.value.genderId ||
        userForm.value.age ||
        userForm.value.filialId
      ) {
        errorMessage.value = false
      }
    })

    watch(
      allProgramGroups,
      () => {
        errorMessage.value = null
      },
      { deep: true }
    )

    watchEffect(() => {
      // console.log('allProgramGroups.value', allProgramGroups.value)
      // console.log('selectedProgramGroups', selectedProgramGroups.value)
    })

    async function scrollToApp () {
      await nextTick()
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          document.documentElement.scrollTop = 0
        })
      })
    }

    return {
      isLoading,
      programsRegularCheckup,
      programsPersonalCheckup,
      userForm,
      currentStep,
      errorMessage,
      checkupTypesList,
      agesList,
      genderList,
      programGroups,
      filialsList,
      updateSelectedProgramGroups,
      handleNextStepBtn,
      onUserFormChange,
      scrollToApp,
      programsRegularSelected
    }
  }
}
</script>

<style scoped lang="scss">
.program-selector__wrapper {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

@media (max-width: 575px) {
  .program-selector__wrapper {
    padding-bottom: 90px;
  }
}

.program-selector__title-text {
  color: #002856;
  font-size: 32px;
  line-height: 40px;
  font-weight: 400;
}

@media (max-width: 1109px) {
  .program-selector__title-text {
    font-size: 24px;
    line-height: 32px;
    font-weight: 700;
  }
}

.program-selector {
  flex-grow: 1;
}

.buttons-wrapper {
  margin-top: 24px;
  display: flex;
  gap: 16px;
  position: relative;
}

.program-selector__error-block {
  position: absolute;
  top: -25px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px 0 5px 66px;
}

@media (max-width: 575px) {
  .buttons-wrapper {
    position: fixed;
    bottom: 90px;
    left: 0;
    z-index: 1;
    width: 100%;
    background-color: #ffffff;
    align-items: center;
    padding: 8px;
  }
  .program-selector__reset-btn {
    width: auto;
    flex-grow: 1;
  }

  .program-selector__error-block {
    margin-top: 10px;
    position: absolute;
    top: -33px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    padding: 5px 0 5px 46px;
  }
}

.program-selector__reset-btn {
  border: none;
  color: #002856;
  font-size: 16px;
  font-weight: 400;
  line-height: 23.58px;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e1e8f4;
  border-radius: 30px;
  height: 50px;
  width: 100%;
  text-transform: uppercase;
  transition: 0.2s;
}

.program-selector__reset-btn:hover {
  background: #bac7de;
}

.program-selector__result {
  scroll-margin-top: 50px;
}

.program-selector__result a {
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #002856;
  border-radius: 30px;
  height: 50px;
  color: #fff;
  text-transform: uppercase;
  transition: 0.2s;
  margin-top: 20px;
}

.program-selector__result a:hover {
  background: #7f8da9;
  text-decoration: none;
}

.program-selector__result h3 {
  font-size: 18px;
  font-weight: 700;
  line-height: 18px;
  text-align: left;
  color: #002856;
  margin-bottom: 16px;
}

.question-block__label-icon-btn {
  border: 1px solid #bac7de;
  height: 163px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.question-block__label-icon-btn.active {
  border-color: #002856;
}

.question-block__label-icon {
  display: inline-flex;
  background: #e1e8f4;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  margin-bottom: 24px;
  align-items: center;
  justify-content: center;
}

.question-block__label-icon svg {
  width: 60%;
  height: 60%;
  fill: #e1e8f4;
}

.question-block__label-icon img {
  width: 60% !important;
  height: 60%;
  fill: #e1e8f4;
}

.question-block ul li img.active {
  border: 1px solid #002856;
}

.program-selector h1 {
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
  text-align: left;
  color: #002856;
  margin-bottom: 48px;
}

.question-block__top input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.question-block__label-text {
  margin-left: 20px;
  font-weight: 400;
  font-style: Regular;
  font-size: 18px;
  line-height: 100%;
  letter-spacing: 0%;
  vertical-align: middle;
  text-align: left;
}

.question-block__label {
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
  user-select: none;
  cursor: pointer;
}

.question-block__label input {
  position: absolute;
  opacity: 0;
  height: 0;
  width: 0;
}

.question-block__checkmark {
  position: absolute;
  bottom: 4px;
  left: 0;
  height: 15px;
  width: 15px;
  border: 1px solid #808080;
  border-radius: 50%;
}

.question-block__checkmark-program {
  top: 170px;
}

.question-block__label input:checked ~ .question-block__checkmark {
  background-color: #fff;
}

.question-block__checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

.question-block__label input:checked ~ .question-block__checkmark:after {
  display: block;
}

.question-block__label .question-block__checkmark:after {
  top: 3px;
  left: 3px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #002856;
}

.question-block__top {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}

.question-block__top span {
  font-size: 24px;
  font-weight: 400;
  line-height: 50px;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  background: #002856;
  border-radius: 50%;
}

.question-block h2 {
  font-size: 18px;
  font-weight: 700;
  line-height: 18px;
  color: #002856;
}

.question-block ul {
  display: grid;
  grid-template-columns: 1fr 1fr;
  row-gap: 30px;
  column-gap: 30px;
  list-style: none;
}

.question-block ul li img {
  width: 100%;
}

.question-block__first {
  margin-bottom: 0;
  border-bottom: 0;
}

.question-block__radio-wrapper {
  display: block !important;
}

.question-block__radio-wrapper li {
  display: flex;
  gap: 60px;
}

.question-block__selector {
  border: 0;
}

@media (max-width: 575px) {
  .question-block__radio-wrapper li {
    flex-direction: column;
    gap: 16px;
  }

  .question-block ul {
    grid-template-columns: 1fr;
  }

  .program-selector h1 {
    margin-bottom: 24px;
  }
}

.slick-arrow {
  margin: 0 !important;
  min-width: 50px !important;
  max-height: 50px !important;
  background-color: #eff4fd !important;
  border: none !important;
  cursor: pointer;
}

.slick-arrow:hover {
  background-color: #bac7de !important;
}
.icon-arrow-nav {
  transform: rotate(180deg);
}

.disabled-button {
  background-color: #fafcfe !important;
}
.disabled-button:hover {
  background-color: #fafcfe !important;
}
.disabled-icon {
  filter: brightness(0) saturate(100%) invert(84%) sepia(17%) saturate(173%)
    hue-rotate(165deg) brightness(89%) contrast(89%);
}

.question-select {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
}

.question-select__label {
  font-weight: 400;
  font-style: Regular;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0%;
  vertical-align: middle;
  color: #7f8da9;
}
.question-select__button-select {
  width: 100%;
  display: flex;
  gap: 8px;
}

.question-select__list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.question-select__gender-placeholder {
  height: 30px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  font-weight: 400;
  font-style: Regular;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0%;
  vertical-align: middle;
  color: #7f8da9;
}

.program-btn-prev {
  background: #e1e8f4;
  width: 50px;
  height: 50px;
  border-radius: 100%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  &:hover:not(.disabled) {
    background-color: #bac7de;
  }
  &.disabled {
    background: rgba(#e1e8f4, 0.7);
    opacity: 0.7;
    cursor: auto;
    &:hover {
      color: rgba(#062e4f, 0.7);
    }
  }
}

.program-btn-prev-icon {
  width: 16px;
  height: 16px;
  transform: rotate(180deg);
}
</style>
